import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Trash2, ChevronRight, Phone, Mail, Star, Pencil, X, Building2, User, MapPin, Globe, Plus, Briefcase } from "lucide-react";
import EmptyState from "../components/EmptyState";
import PartnerForm, { OsobaPole, Sheet, chybaOsoby } from "../components/PartnerForm";
import ClientForm from "../components/ClientForm";
import { Button, Spinner } from "../components/ui/kit";
import { FazePill, etapa } from "../lib/faze";
import { fmtKc } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { canSee } from "../lib/nav";
import api, { apiError } from "../lib/api";
import { Drobecky, useNazevStranky, useUrlStav } from "../lib/navigace";
import { inicialy } from "./Partneri";
import DetailHlava, { KartaZalozky, KartaSekce, Pridat } from "../components/DetailHlava";

const TABS = [
  { key: "klienti", label: "Klienti", pocet: "klientu" },
  { key: "pripady", label: "Obchodní případy", kratce: "Případy", pocet: "pripadu" },
  { key: "osoby", label: "Kontaktní osoby", kratce: "Osoby" },
];

const PRAZDNA_OSOBA = { jmeno: "", prijmeni: "", pozice: "", telefon: "", email: "" };

export default function PartnerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const canWrite = canSee(user, ["OWNER", "OBCHODNIK"]);
  const isOwner = canSee(user, ["OWNER"]);
  const [p, setP] = useState(undefined);
  const [tab, setTab] = useUrlStav("tab", "klienti");
  const [edit, setEdit] = useState(false);
  const [novyKlient, setNovyKlient] = useState(false);
  const [osoba, setOsoba] = useState(null);        // {id?, ...pole} = dialog osoby
  const [chyba, setChyba] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/api/partneri/${id}`); setP(data); }
    catch { setP(null); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useNazevStranky(p?.nazev);

  const ulozOsobu = async () => {
    const ch = chybaOsoby(osoba);
    if (ch) return setChyba(ch);
    setBusy(true); setChyba("");
    try {
      const { id: oid, ...pole } = osoba;
      if (oid) await api.patch(`/api/partneri/${id}/osoby/${oid}`, pole);
      else await api.post(`/api/partneri/${id}/osoby`, pole);
      setOsoba(null); load();
    } catch (e) { setChyba(apiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const smazOsobu = async (o) => {
    if (!window.confirm(`Odebrat kontaktní osobu ${[o.jmeno, o.prijmeni].filter(Boolean).join(" ")}?`)) return;
    try { await api.delete(`/api/partneri/${id}/osoby/${o.id}`); load(); }
    catch (e) { window.alert(apiError(e.response?.data?.detail) || e.message); }
  };
  const hlavni = async (o) => { await api.post(`/api/partneri/${id}/osoby/${o.id}/hlavni`); load(); };
  const archivuj = async () => {
    if (!window.confirm("Archivovat partnera? Klienti zůstanou, jen u nich nebude uvedený partner.")) return;
    await api.delete(`/api/partneri/${id}`);
    nav("/partneri");
  };

  if (p === undefined) return <div className="p-10 flex justify-center text-muted"><Spinner /></div>;
  if (p === null)
    return <div className="card p-8"><EmptyState icon="Handshake" title="Partner nenalezen"
      description="Partner neexistuje nebo byl archivován."
      action={<button onClick={() => nav("/partneri")} className="btn-ghost">Zpět na partnery</button>} /></div>;

  const a = p.adresa || {};
  const sidlo = [a.ulice, [a.psc, a.mesto].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  const osoby = p.kontaktni_osoby || [];
  const otevrene = p.pripady.filter((x) => ["NOVY", "ZAMERENI", "NABIDKA"].includes(x.faze)).length;
  const web = p.web && (p.web.startsWith("http") ? p.web : `https://${p.web}`);
  const akceTabu = canWrite && {
    klienti: { text: "+ Klient", fn: () => setNovyKlient(true), testid: "partner-novy-klient" },
    osoby: { text: "+ Osoba", fn: () => { setChyba(""); setOsoba({ ...PRAZDNA_OSOBA }); }, testid: "partner-nova-osoba" },
  }[tab];

  const kontakt = osoby[0];
  const kontaktJmeno = kontakt && [kontakt.jmeno, kontakt.prijmeni].filter(Boolean).join(" ");
  const akceZalozky = akceTabu && <Pridat text={akceTabu.text.replace("+ ", "")} onClick={akceTabu.fn} testid={akceTabu.testid} />;

  return (
    <div data-testid="partner-detail" className="flex flex-col gap-4">
      <Drobecky cesta={[{ nazev: "Obchodní partneři", to: "/partneri", testid: "partner-zpet" }]} aktualni={p.nazev} />

      <DetailHlava testid="partner-hlava" druh="Obchodní partner" nazev={p.nazev}
        obrazek={p.logo_url
          ? <span className="h-12 w-12 md:h-14 md:w-14 rounded-[16px] bg-white flex items-center justify-center overflow-hidden" data-testid="partner-logo-hlava">
              <img src={p.logo_url} alt="" className="h-full w-full object-contain" /></span>
          : <span className="h-12 w-12 md:h-14 md:w-14 rounded-[16px] bg-white/10 text-white text-[17px] md:text-[19px] font-800 flex items-center justify-center">{inicialy(p.nazev)}</span>}
        meta={[
          kontaktJmeno && <span className="inline-flex items-center gap-1.5"><User size={13} />Kontakt <span className="font-600 text-white">{kontaktJmeno}</span></span>,
          kontakt?.telefon && <a href={`tel:${kontakt.telefon}`} className="amount inline-flex items-center gap-1.5 hover:text-white"><Phone size={13} />{kontakt.telefon}</a>,
          kontakt?.email && <a href={`mailto:${kontakt.email}`} className="inline-flex items-center gap-1.5 hover:text-white"><Mail size={13} />{kontakt.email}</a>,
          sidlo && <span className="inline-flex items-center gap-1.5"><MapPin size={13} />{sidlo}</span>,
          web && <a href={web} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-white"><Globe size={13} />{p.web}</a>,
        ].filter(Boolean)}
        vedlejsi={canWrite ? [{ text: "Upravit", onClick: () => setEdit(true), testid: "partner-edit" }] : []}
        hlavni={canWrite ? { text: "Nový klient", ikona: Plus, onClick: () => setNovyKlient(true), testid: "partner-hlava-novy-klient" } : null}
        vice={[
          ...(canWrite ? [{ text: "Upravit partnera", ikona: Pencil, onClick: () => setEdit(true), testid: "partner-vice-upravit" }] : []),
          ...(isOwner ? [{ text: "Archivovat partnera", ikona: Trash2, nebezpecne: true, onClick: archivuj, testid: "partner-delete" }] : []),
        ]}
        cta={[
          ...(canWrite ? [{ text: "Klient", ikona: Plus, onClick: () => setNovyKlient(true), testid: "partner-cta-klient" }] : []),
          ...(kontakt?.telefon ? [{ text: "Zavolat", ikona: Phone, href: `tel:${kontakt.telefon}`, testid: "partner-cta-zavolat" }] : []),
          ...(kontakt?.email ? [{ text: "E-mail", ikona: Mail, href: `mailto:${kontakt.email}`, testid: "partner-cta-email" }] : []),
        ]}
        /* Obrat = objednané technologie klientů od partnera, bez DPH (jako na kartě klienta). */
        dlazdice={[
          ...(p.muze_ceny ? [{ popisek: "Obrat od partnera", hodnota: fmtKc(p.obrat_kc || 0), jednotka: "Kč", pod: "objednané zakázky, bez DPH", testid: "partner-obrat" }] : []),
          { popisek: "Klientů", hodnota: String(p.klientu), pod: p.klientu ? "dohozených klientů" : "zatím žádný klient", testid: "partner-klientu" },
          { popisek: "Obchodních případů", hodnota: String(p.pripadu), pod: otevrene ? `${otevrene} rozpracovaných` : "nic rozpracovaného", testid: "partner-pripadu" },
        ]} />

      <div className="grid lg:grid-cols-[minmax(0,1fr)_340px] gap-4 items-start">
        <KartaZalozky testid="partner-zalozky" aktivni={tab} onZmena={setTab} akce={akceZalozky}
          zalozky={TABS.map((t) => ({ key: t.key,
            // Na telefonu krátký název, ať se tři záložky i „+ Přidat" vejdou bez posouvání.
            label: t.kratce ? <><span className="md:hidden">{t.kratce}</span><span className="hidden md:inline">{t.label}</span></> : t.label, pocet: t.pocet ? p[t.pocet] : osoby.length, testid: `partner-tab-${t.key}` }))}>
          {tab === "klienti" && (p.klienti.length === 0 ? (
            <EmptyState icon="Users" title="Zatím žádní klienti"
              description="Klienta k partnerovi přiřadíš v jeho kartě (Upravit → Obchodní partner) nebo tady přes „Nový klient“." />
          ) : p.klienti.map((c) => (
            <button key={c.id} onClick={() => nav(`/klienti/${c.id}`)} data-testid={`partner-klient-${c.id}`}
              className="w-full text-left flex items-center gap-3.5 px-4 md:px-5 py-3 border-b border-line-soft last:border-0 hover:bg-inputbg">
              <span className="h-9 w-9 rounded-[12px] bg-inputbg text-sec flex items-center justify-center shrink-0">
                {c.typ === "FIRMA" ? <Building2 size={16} strokeWidth={1.9} /> : <User size={16} strokeWidth={1.9} />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block t-telo font-600 text-ink truncate">{c.display_name}</span>
                <span className="block t-meta text-muted truncate">
                  {[c.mesto, c.telefon, c.email].filter(Boolean).join(" · ") || "bez kontaktu"}
                </span>
              </span>
              <span className="t-meta text-sec-3 shrink-0"><span className="amount font-700 text-ink">{c.pripadu}</span> {c.pripadu === 1 ? "případ" : "případů"}</span>
              <ChevronRight size={15} className="text-muted-2 shrink-0" />
            </button>
          )))}

          {tab === "pripady" && (p.pripady.length === 0 ? (
            <EmptyState icon="Briefcase" title="Žádné obchodní případy"
              description="Případy klientů od tohoto partnera se tu objeví samy." />
          ) : p.pripady.map((x) => (
            <button key={x.id} onClick={() => nav(`/obchod/${x.id}`)} data-testid={`partner-pripad-${x.id}`}
              className="w-full text-left flex items-center gap-3.5 px-4 md:px-5 py-3 border-b border-line-soft last:border-0 hover:bg-inputbg">
              <span className="h-9 w-9 rounded-[12px] bg-inputbg text-sec flex items-center justify-center shrink-0"><Briefcase size={16} strokeWidth={1.9} /></span>
              <span className="min-w-0 flex-1">
                <span className="block t-telo font-600 text-ink truncate">{x.nazev}</span>
                <span className="block t-meta text-muted truncate">
                  <span className="amount">{x.cislo}</span> · {x.client_name}
                  {x.technologie.length ? ` · ${x.technologie.join(" · ")}` : ""}
                </span>
              </span>
              {x.hodnota_kc !== undefined && (
                <span className="amount t-telo font-700 text-ink hidden sm:block">{fmtKc(x.hodnota_kc)} Kč</span>
              )}
              <FazePill faze={etapa(x)} />
            </button>
          )))}

          {tab === "osoby" && osoby.length === 0 && (
            <EmptyState icon="Users" title="Zatím bez kontaktní osoby"
              description="Až budeš vědět, s kým u partnera jednáš, přidej ho tady." />
          )}
          {tab === "osoby" && osoby.map((o, i) => (
            <div key={o.id} className="flex items-start gap-3.5 px-4 md:px-5 py-3 border-b border-line-soft last:border-0"
              data-testid={`partner-osoba-${o.id}`}>
              <span className="h-9 w-9 rounded-full bg-inputbg t-meta font-700 text-sec flex items-center justify-center shrink-0">
                {inicialy([o.jmeno, o.prijmeni].filter(Boolean).join(" "))}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2 t-telo font-600 text-ink">
                  <span className="truncate">{[o.jmeno, o.prijmeni].filter(Boolean).join(" ")}</span>
                  {i === 0 && <span className="label-up px-2 py-0.5 rounded-pill bg-[#f2f2ef] text-sec-3 shrink-0">Hlavní</span>}
                </span>
                {o.pozice && <span className="block t-meta text-muted">{o.pozice}</span>}
                <span className="mt-1 flex flex-wrap gap-x-4 gap-y-1 t-meta">
                  {o.telefon && <a href={`tel:${o.telefon}`} className="tap md:min-h-0 inline-flex items-center gap-1 text-sec hover:text-ink amount"><Phone size={13} />{o.telefon}</a>}
                  {o.email && <a href={`mailto:${o.email}`} className="tap md:min-h-0 inline-flex items-center gap-1 text-sec hover:text-ink break-all"><Mail size={13} />{o.email}</a>}
                </span>
              </span>
              {canWrite && (
                <span className="flex items-center gap-0.5 shrink-0">
                  {i > 0 && (
                    <button onClick={() => hlavni(o)} title="Nastavit jako hlavní kontakt" aria-label="Nastavit jako hlavní"
                      className="tap md:min-h-0 h-8 w-8 flex items-center justify-center text-muted hover:text-ink"><Star size={14} /></button>
                  )}
                  <button onClick={() => { setChyba(""); setOsoba({ ...PRAZDNA_OSOBA, ...o }); }} aria-label="Upravit osobu"
                    className="tap md:min-h-0 h-8 w-8 flex items-center justify-center text-muted hover:text-ink"><Pencil size={14} /></button>
                  <button onClick={() => smazOsobu(o)} aria-label="Odebrat osobu"
                    className="tap md:min-h-0 h-8 w-8 flex items-center justify-center text-muted hover:text-danger"><X size={15} /></button>
                </span>
              )}
            </div>
          ))}
        </KartaZalozky>

        <aside className="flex flex-col gap-4 min-w-0" data-testid="partner-udaje">
          <KartaSekce titul="Firemní údaje" telo
            akce={canWrite && <Pridat text="Upravit" onClick={() => setEdit(true)} testid="partner-udaje-upravit" />}>
            {[["Sídlo", sidlo], ["IČO", p.ico], ["DIČ", p.dic]].map(([k, v], i, arr) => (
              <div key={k} className={`flex justify-between gap-3 py-2.5 t-meta md:text-[13px] ${i < arr.length - 1 ? "border-b border-line-soft" : ""}`}>
                <span className="text-sec shrink-0">{k}</span>
                <span className={`font-700 text-ink text-right ${k === "Sídlo" ? "" : "amount"}`}>{v || "—"}</span>
              </div>
            ))}
          </KartaSekce>
          {p.poznamky && (
            <KartaSekce titul="Poznámka" telo>
              <div className="t-telo md:text-[13.5px] leading-[1.55] text-sec whitespace-pre-line">{p.poznamky}</div>
            </KartaSekce>
          )}
        </aside>
      </div>

      {edit && <PartnerForm partner={p} onClose={() => setEdit(false)} onSaved={load} />}
      {novyKlient && <ClientForm defaultPartnerId={p.id} onClose={() => setNovyKlient(false)} onSaved={load} />}
      {osoba && (
        <Sheet titulek={osoba.id ? "Upravit kontaktní osobu" : "Nová kontaktní osoba"} onClose={() => setOsoba(null)} testid="osoba-form">
          <div className="p-6 space-y-4">
            <OsobaPole o={osoba} setO={setOsoba} />
            {chyba && <div className="t-meta text-danger">{chyba}</div>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setOsoba(null)} className="btn-ghost">Zrušit</button>
              <Button onClick={ulozOsobu} disabled={busy} data-testid="osoba-save">{busy ? <Spinner /> : "Uložit"}</Button>
            </div>
          </div>
        </Sheet>
      )}
    </div>
  );
}
